import type { CardElementStyle } from '@basis-theory/web-elements';

import type { CardCollectionFieldPresentation } from './card-collection-provider';
import type {
  PaypercutElementsAppearance,
  PaypercutElementsAppearanceRuleSelector,
} from '../types/elements';
import { isSafeElementsAppearanceValue } from './appearance';

/**
 * Versioned visual contract for SDK-owned card fields.
 *
 * V2 preserves Hosted Checkout's Elements tokens and semantic rules while
 * superseding its full-width-provider badge overlay with an extension-safe,
 * two-region SDK layout. Keeping the provenance executable makes this
 * temporary cross-repository duplication reviewable until both runtimes
 * consume one shared package. The file digests identify the original captured
 * working-tree sources; the Git revision is only their baseline because part
 * of that source was not committed yet.
 */
export const CARD_FIELD_APPEARANCE_CONTRACT = {
  version: 2,
  sourceRepository: 'hosted-checkout',
  sourceWorktree: 'singular-elements',
  sourceBaselineRevision: 'dacfd3cf2ba170ad49613e15056d19505a95ea13',
  sourceFiles: {
    compiler: {
      path: 'src/components/elements/appearance/compiler.ts',
      sha256: '4aaf21ac8c4e2a83958e1c5a9565cfb5aca0d03cba2a6e0e0cda5b8db7e514cc',
    },
    providerStyle: {
      path: 'src/components/elements/payment/basisTheoryCardStyle.ts',
      sha256: 'fa8a7ebeb755823671781ab60a32edd881900699537469bb4b075d36be48cabc',
    },
    stylesheet: {
      path: 'src/styles/elements.css',
      sha256: '1e9f645c2eed5bee1a17c1d0ef031c9caf4c35e9ee70be6bb3d7efce7ce64ba2',
    },
  },
} as const;

const LIGHT_VARIABLES = {
  colorPrimary: '#0570de',
  colorPrimaryText: '#ffffff',
  colorBackground: '#ffffff',
  colorSurface: '#ffffff',
  colorText: '#2b2d2f',
  colorTextSecondary: '#6a6c6f',
  colorTextPlaceholder: '#737474',
  colorDanger: '#cc1818',
  colorSuccess: '#30b130',
  colorWarning: '#e69e2c',
  colorBorder: '#d7dde0',
  colorFocus: '#0570de',
  colorMuted: '#f2f4f5',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontSizeBase: '16px',
  fontWeightNormal: '400',
  fontWeightMedium: '500',
  lineHeight: '1.5',
  spacingUnit: '4px',
  gridColumnSpacing: '12px',
  gridRowSpacing: '12px',
  labelSpacing: '6px',
  borderRadius: '4px',
  buttonBorderRadius: '4px',
  buttonExpressCheckoutBorderRadius: '4px',
  buttonPadding: '12px',
} as const;

const DARK_VARIABLES = {
  ...LIGHT_VARIABLES,
  colorBackground: '#15171c',
  colorSurface: '#1d2027',
  colorText: '#f3f4f6',
  colorTextSecondary: '#aeb7c7',
  colorTextPlaceholder: '#8f99aa',
  colorBorder: '#3a404b',
  colorFocus: '#7db7ff',
  colorMuted: '#252932',
} as const;

export type CardFieldAppearanceVariables = {
  [Name in keyof typeof LIGHT_VARIABLES]: string;
};

export interface ResolvedCardFieldAppearance {
  theme: 'light' | 'dark';
  disableAnimations: boolean;
  variables: CardFieldAppearanceVariables;
}

const VARIABLE_NAMES = new Set<string>(Object.keys(LIGHT_VARIABLES));
const PUBLIC_VARIABLE_REFERENCE = /var\(\s*--([A-Za-z][A-Za-z0-9]*)\s*\)/g;

export function resolveCardFieldAppearance(
  appearance: PaypercutElementsAppearance | undefined,
): ResolvedCardFieldAppearance {
  const theme = appearance?.theme ?? 'light';
  const themeVariables = theme === 'dark' ? DARK_VARIABLES : LIGHT_VARIABLES;
  const overrides = Object.fromEntries(
    Object.entries(appearance?.variables ?? {}).filter(
      ([name, value]) => VARIABLE_NAMES.has(name) && typeof value === 'string',
    ),
  );

  return {
    theme,
    disableAnimations: appearance?.disableAnimations ?? false,
    variables: {
      ...themeVariables,
      ...overrides,
    },
  } as ResolvedCardFieldAppearance;
}

/** Resolve one semantic public rule to concrete provider/DOM style values. */
export function resolveCardFieldAppearanceRule(
  appearance: PaypercutElementsAppearance | undefined,
  selector: PaypercutElementsAppearanceRuleSelector,
): Record<string, string> {
  const rule = appearance?.rules?.[selector];
  if (!rule) return {};
  const variables = resolveCardFieldAppearance(appearance).variables;

  return Object.fromEntries(
    Object.entries(rule).flatMap(([property, value]) => {
      if (typeof value !== 'string' || !isSafeElementsAppearanceValue(value)) return [];
      return [
        [
          property,
          value.replace(PUBLIC_VARIABLE_REFERENCE, (match, name: string) =>
            name in variables ? variables[name as keyof CardFieldAppearanceVariables] : match,
          ),
        ],
      ];
    }),
  );
}

/**
 * Compile the secure Basis Theory input style from the Hosted Checkout token
 * baseline. Provider fields cannot inherit merchant-document CSS, so every
 * public token and variable reference is resolved before crossing the provider
 * boundary. Physical end-adornment separation is owned by CardFieldSurface.
 */
export function createBasisTheoryCardFieldStyle(
  appearance: PaypercutElementsAppearance | undefined,
  presentation?: CardCollectionFieldPresentation,
): CardElementStyle {
  const variables = resolveCardFieldAppearance(appearance).variables;
  const inputRule = resolveCardFieldAppearanceRule(appearance, '.Input');
  const invalidRule = resolveCardFieldAppearanceRule(appearance, '.Input--invalid');
  const inputBackgroundColor = inputRule.backgroundColor ?? variables.colorSurface;
  const inputColor = inputRule.color ?? variables.colorText;
  const invalidBackgroundColor = invalidRule.backgroundColor ?? inputBackgroundColor;
  const invalidColor = invalidRule.color ?? inputColor;
  const chromeIosAttributeAutofill =
    typeof navigator !== 'undefined' && /\bCriOS\//.test(navigator.userAgent);
  const providerStateValue = (value: string): string =>
    chromeIosAttributeAutofill ? `${value} !important` : value;

  return {
    base: {
      // Basis Theory v2 derives its high-specificity browser-autofill paint
      // from the active state's color and backgroundColor. An explicitly
      // opaque surface prevents Chrome/WebKit's native blue fill from showing
      // through only the provider iframe while the SDK-owned badge lane keeps
      // the configured surface color.
      backgroundColor: inputBackgroundColor,
      color: inputColor,
      fontFamily: variables.fontFamily,
      fontSize: variables.fontSizeBase,
      fontWeight: variables.fontWeightNormal,
      lineHeight: variables.lineHeight,
      // The provider is clipped to its own physical region when card brands
      // are visible. Keep ordinary input padding here so provider- or
      // extension-owned controls can use that region's far-right edge.
      padding: !presentation
        ? '12px'
        : presentation.floatingLabels
          ? presentation.floatingLabelActive
            ? '23px 12px 9px 12px'
            : '16px 12px 16px 12px'
          : '12px',
      '::placeholder': {
        color: variables.colorTextPlaceholder,
      },
      ':disabled': {
        backgroundColor: inputBackgroundColor,
      },
      ...inputRule,
    },
    empty: {
      // Chrome on iOS injects a later `[chrome-autofilled]` rule with an
      // !important blue background inside each secure provider iframe. Basis
      // Theory v2 filters custom attribute selectors, but its empty/invalid/
      // complete state rules are deliberately class-qualified three times.
      // On CriOS only, matching importance lets those supported state rules
      // outrank Chrome without disabling native saved-card autofill.
      backgroundColor: providerStateValue(inputBackgroundColor),
      color: providerStateValue(inputColor),
    },
    invalid: {
      // Paypercut owns the visible validation treatment. Provider text remains
      // neutral while incomplete input is being edited.
      ...invalidRule,
      backgroundColor: providerStateValue(invalidBackgroundColor),
      color: providerStateValue(invalidColor),
    },
    complete: {
      backgroundColor: providerStateValue(inputBackgroundColor),
      color: providerStateValue(inputColor),
    },
  } as CardElementStyle;
}
